import React, { Suspense, useLayoutEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { useProfile } from '@/entities/profile';
import { ROLES } from '@/entities/profile/model/constants';
import { ROUTES_PATH } from '@/shared/constants/routes';
import { Loader, useLoader } from '@/shared/ui/Loader';
import { Logo } from '@/shared/ui/Logo';
import { MaxWidthContainer } from '@/shared/ui/PageContainer';
import { getRoutePath } from '@/shared/utils/routes';

import {
  StyledContainer,
  StyledContentBlock,
  StyledHeaderBox,
  StyledLayoutContainer,
  StyledMainBox,
  StyledNavBlock,
} from './styles/privateLayoutStyles';
import { Header } from './Header';
import { Navigation } from './Navigation';
import { SideContainer } from './SideContainer';

const CHAT_ONLY_ROLES = new Set<string>([ROLES.OPERATOR, ROLES.AUTHORIZED]);

export const PrivateLayout = () => {
  const { profile, isLoading } = useProfile();
  const { isShowLoader } = useLoader();
  const location = useLocation();
  const chatsRoutePath = `/${ROUTES_PATH.MAIN.CHATS}`;
  const isFullscreenRoute =
    location.pathname === chatsRoutePath ||
    location.pathname.startsWith(`${chatsRoutePath}/`);

  useLayoutEffect(() => {
    isShowLoader(isLoading);
    return () => {
      isShowLoader(false);
    };
  }, [isLoading, isShowLoader]);

  if (isLoading) {
    return null;
  }

  if (!profile) {
    return <Navigate to={getRoutePath('AUTH', 'LOGIN')} replace />;
  }

  if (CHAT_ONLY_ROLES.has(profile.role) && !isFullscreenRoute) {
    return <Navigate to={getRoutePath('MAIN', 'CHATS')} replace />;
  }

  const outlet = (
    <Suspense fallback={<Loader containerSize="medium" />}>
      <Outlet />
    </Suspense>
  );

  if (isFullscreenRoute) {
    return (
      <StyledLayoutContainer>
        <StyledContentBlock $fullscreen>
          <StyledMainBox as="main" $fullscreen>
            {outlet}
          </StyledMainBox>
        </StyledContentBlock>
      </StyledLayoutContainer>
    );
  }

  return (
    <StyledLayoutContainer>
      <MaxWidthContainer>
        <StyledContainer>
          <SideContainer>
            <Logo
              variant="mark"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                width: '100px',
                paddingTop: '16px',
                paddingBottom: '16px',
              }}
            />
            <StyledNavBlock>
              <Navigation />
            </StyledNavBlock>
          </SideContainer>

          <StyledContentBlock>
            <StyledHeaderBox>
              <Header />
            </StyledHeaderBox>
            <StyledMainBox as="main">
              {outlet}
            </StyledMainBox>
          </StyledContentBlock>
        </StyledContainer>
      </MaxWidthContainer>
    </StyledLayoutContainer>
  );
};
